import { useEffect, useMemo, useState } from 'react';
import { setStudentSchooling, decideMuqriRequest, listenToDirectory, type DirectoryUser } from '@core/firebase/admin';
import { filterUsers, orderUsers } from '@core/admin/directory';
import { useLanguage } from '@/contexts/LanguageContext';
import { ConfirmButton } from '@/components/ConfirmButton';
import { GraduationCap, Search, ShieldCheck, UserRound, Users } from 'lucide-react';
import {
  EmptyState, IconTile, Notice, PageHeader, Segmented, SoftCard, field, pill,
} from '@/components/PageKit';
import { cn } from '@/lib/cn';

type RoleFilter = 'all' | 'students' | 'muqris' | 'admins';

/** Board a2's role filter, applied after the shared search and order. */
const ROLE_MATCH: Record<RoleFilter, (u: DirectoryUser) => boolean> = {
  all: () => true,
  students: (u) => !u.isMuqri && !u.isAdmin,
  muqris: (u) => u.isMuqri,
  admins: (u) => u.isAdmin,
};

/**
 * Everyone who has registered.
 *
 * Searching and ordering both come from the shared helpers — a local matcher
 * would disagree with the mobile app about who matches, and a directory
 * sorted purely by name buries the only actionable rows under the students
 * who make up almost all of it.
 */
export default function UsersPage() {
  const { t } = useLanguage();
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [term, setTerm] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  /*
   * م-2-06, per row: the grade the admin has picked, and what happened when
   * they pressed Move. Keyed by uid so two rows cannot share one message.
   */
  const [pickedGrade, setPickedGrade] = useState<Record<string, string>>({});
  const [moved, setMoved] = useState<Record<string, boolean>>({});
  const [moveFailed, setMoveFailed] = useState<Record<string, boolean>>({});

  const move = async (u: DirectoryUser) => {
    const grade = Number(pickedGrade[u.uid] ?? u.grade ?? 0);
    if (!Number.isInteger(grade) || grade < 1 || grade > 12) return;
    setMoved((m) => ({ ...m, [u.uid]: false }));
    setMoveFailed((m) => ({ ...m, [u.uid]: false }));
    try {
      // The country comes from the pupil's own record: this control moves a
      // GRADE, and inventing a country here would re-key their curriculum.
      await setStudentSchooling(u.uid, u.country ?? 'SA', grade);
      setMoved((m) => ({ ...m, [u.uid]: true }));
    } catch {
      setMoveFailed((m) => ({ ...m, [u.uid]: true }));
    }
  };

  useEffect(() => listenToDirectory(setUsers, () => setErrorKey('admin.users.failed')), []);

  const [role, setRole] = useState<RoleFilter>('all');
  const shown = useMemo(
    () => orderUsers(filterUsers(users, term)).filter(ROLE_MATCH[role]),
    [users, term, role],
  );

  const revoke = (uid: string) => {
    setErrorKey(null);
    void decideMuqriRequest(uid, 'rejected').catch((e: unknown) => {
      // The server refuses when no request document exists — say which of the
      // two it was, rather than a blanket "try again" for a condition that
      // retrying cannot fix.
      const message = String((e as { message?: string })?.message ?? '');
      setErrorKey(message.includes('no-request') ? 'admin.users.noRequest' : 'admin.users.failed');
    });
  };

  const roleLabel = (u: DirectoryUser) => {
    if (u.isMuqri) return t('admin.users.roleMuqri');
    if (u.isAdmin) return t('admin.users.roleAdmin');
    return t('admin.users.roleStudent');
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <PageHeader title={t('admin.users.title')} subtitle={t('admin.users.subtitle')} />

      {/* Board a2: search and the role filter on one line. */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative min-w-[240px] flex-1">
          <span className="sr-only">{t('admin.users.search')}</span>
          <Search className="pointer-events-none absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2 text-helper" />
          <input
            data-testid="user-search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={t('admin.users.search')}
            className={cn(field, 'rounded-full ps-12')}
          />
        </label>
        <Segmented<RoleFilter>
          label={t('admin.users.filterLabel')}
          value={role}
          onChange={setRole}
          options={[
            { value: 'all', label: t('admin.users.filterAll'), testId: 'role-filter-all' },
            { value: 'students', label: t('admin.users.filterStudents'), testId: 'role-filter-students' },
            { value: 'muqris', label: t('admin.users.filterMuqris'), testId: 'role-filter-muqris' },
            { value: 'admins', label: t('admin.users.filterAdmins'), testId: 'role-filter-admins' },
          ]}
        />
      </div>

      {errorKey && <Notice tone="danger" testId="users-error">{t(errorKey)}</Notice>}

      {users.length === 0 ? (
        <SoftCard as="div">
          <EmptyState
            testId="no-users"
            icon={<Users />}
            title={t('admin.users.emptyTitle')}
            body={t('admin.users.emptyBody')}
          />
        </SoftCard>
      ) : shown.length === 0 ? (
        <SoftCard as="div">
          <EmptyState
            testId="no-matches"
            icon={<Search />}
            title={t('admin.users.noMatchTitle')}
            body={t('admin.users.noMatchBody')}
          />
        </SoftCard>
      ) : (
        <ul className="card-soft divide-y divide-hair px-5">
          {shown.map((u) => {
            const name = u.displayName || u.uid;
            return (
              <li
                key={u.uid}
                data-testid={`user-${u.uid}`}
                className="flex flex-wrap items-center gap-x-4 gap-y-3 py-4"
              >
                <IconTile>{u.isMuqri ? <GraduationCap /> : u.isAdmin ? <ShieldCheck /> : <UserRound />}</IconTile>
                <div className="min-w-[160px] flex-1">
                  <p className="truncate font-semibold text-ink">{name}</p>
                  <p className="text-sm text-helper" dir="ltr">{u.phone}</p>
                </div>
                <span
                  data-testid={`role-${u.uid}`}
                  className={cn(
                    'shrink-0 rounded-full px-3 py-1 text-xs font-semibold',
                    u.isMuqri ? 'bg-lime text-accent-foreground' : 'bg-surface text-helper',
                  )}
                >
                  {roleLabel(u)}
                </span>
                {/*
                  * م-2-06. Only a pupil has a grade: a مُقرئ or an admin is
                  * not measured against a curriculum, and the server
                  * refuses to give them one.
                  */}
                {!u.isMuqri && !u.isAdmin && (
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-helper" htmlFor={`grade-${u.uid}`}>
                        {t('admin.users.schooling')}
                      </label>
                      <select
                        id={`grade-${u.uid}`}
                        data-testid={`grade-${u.uid}`}
                        value={pickedGrade[u.uid] ?? (u.grade === null ? '' : String(u.grade))}
                        onChange={(e) => setPickedGrade((g) => ({ ...g, [u.uid]: e.target.value }))}
                        className={cn(field, 'w-20 rounded-full px-3 py-2')}
                      >
                        <option value="">—</option>
                        {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((g) => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        data-testid={`move-${u.uid}`}
                        onClick={() => { void move(u); }}
                        className={cn(pill.base, pill.sm, pill.quiet)}
                      >
                        {t('admin.users.moveGrade')}
                      </button>
                    </div>
                    {/* The promise م-2-06 makes, where the decision is made. */}
                    <p data-testid={`moved-note-${u.uid}`} className="text-xs text-helper">
                      {t('admin.users.moved')}
                    </p>
                    {moved[u.uid] && (
                      <p data-testid={`moved-${u.uid}`} role="status" className="text-xs text-success">
                        {t('admin.users.moved')}
                      </p>
                    )}
                    {moveFailed[u.uid] && (
                      <p data-testid={`move-error-${u.uid}`} role="alert" className="text-xs text-danger">
                        {t('admin.users.moveFailed')}
                      </p>
                    )}
                  </div>
                )}
                {u.isMuqri && (
                  <ConfirmButton
                    testId={`revoke-${u.uid}`}
                    tone="danger"
                    size="sm"
                    label={t('admin.users.revoke')}
                    question={t('admin.users.confirmRevoke', { name })}
                    onConfirm={() => revoke(u.uid)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
